/** @jsxImportSource theme-ui @jsxRuntime classic */
import React from 'react';
import { Block } from '@sanity/block-content-to-react';
import { IGatsbyImageData } from 'gatsby-plugin-image';

import {
  Box,
  Flex,
  Section,
  TishmanThemeName,
  Text,
  SanityRichText,
  Link,
  IntrinsicImage,
} from '@tishman/components';

interface CalloutRowsProps {
  calloutRows:
    | readonly ({
        readonly _key: string | null;
        readonly title: string | null;
        readonly colorSchema: string | null;
        readonly _rawLongText: Record<string, unknown> | null;
        readonly image: {
          readonly alt: string | null;
          readonly asset: {
            readonly gatsbyImageData: IGatsbyImageData;
          } | null;
        } | null;
        readonly cta: {
          readonly url: string | null;
          readonly caption: string | null;
        } | null;
      } | null)[];
}

export const CalloutRows = ({ calloutRows }: CalloutRowsProps) => {
  return calloutRows?.map((callout, index) => {
    const theme = (callout?.colorSchema ?? 'Rock Center') as TishmanThemeName;
    const linkSx =
      theme === 'Rock Center Dark Gold' ? { color: '#000000' } : {};

    return (
      <Section key={index} theme={theme}>
        <Box>
          <Flex
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: ['24px', '64px'],
              padding: ['64px 16px', '64px 32px'],
              flexDirection: [
                'column-reverse',
                'column-reverse',
                index % 2 ? 'row-reverse' : 'row',
              ],
              maxWidth: '1200px',
              margin: '0 auto',
            }}
          >
            <Flex
              sx={{
                flexDirection: 'column',
                gap: '30px',
                flex: 1,
                flexGrow: 1,
                alignItems: 'flex-start',
              }}
            >
              <Flex
                sx={{
                  flexDirection: 'column',
                  gap: '16px',
                  alignSelf: 'stretch',
                  width: '100%',
                  flex: '0 0 auto',
                  alignItems: 'flex-start',
                }}
              >
                <Text
                  sx={{
                    alignSelf: 'stretch',
                    marginTop: '-1px',
                  }}
                  variant="h2"
                >
                  {callout?.title}
                </Text>

                <SanityRichText
                  blocks={(callout?._rawLongText ?? []) as unknown as Block[]}
                  components={{
                    p: ({ children }) => (
                      <Text
                        sx={{
                          fontSize: 3,
                          lineHeight: [1.16, 1.17],
                        }}
                        variant="body"
                      >
                        {children as React.ReactNode}
                      </Text>
                    ),
                  }}
                />
              </Flex>

              {callout?.cta?.url && callout?.cta?.caption && (
                <Link
                  sx={{ letterSpacing: '2px', ...linkSx }}
                  to={callout?.cta?.url || ''}
                  variant="button"
                >
                  {callout?.cta?.caption}
                </Link>
              )}
            </Flex>

            <Flex
              sx={{
                flexDirection: 'column',
                alignItems: index % 2 === 0 ? 'flex-end' : 'flex-start',
                flex: '1 0 0',
                width: '100%',
                maxHeight: '268px',
              }}
            >
              {callout?.image && callout.image.asset?.gatsbyImageData && (
                <IntrinsicImage
                  alt={callout.image.alt || ''}
                  image={callout.image.asset.gatsbyImageData}
                  sx={{
                    width: '100%',
                    height: '100%',
                    aspectRatio: '16/9',
                  }}
                />
              )}
            </Flex>
          </Flex>
        </Box>
      </Section>
    );
  });
};
